import CheckIcon from "@mui/icons-material/Check";
import { Box, LinearProgress, Slide, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";

import { FlaggedListItem } from "components/detector/FlaggedListItem";
import { SeeAllLink } from "components/detector/SeeAllLink";
import { BoundingBoxesProvider } from "context/boundingBoxesContext";
import { QueryDetailsModal } from "features/queryDetailsModal/QueryDetailsModal";
import { useAppDispatch } from "hooks/store";
import { Typography } from "new-components/typography/Typography";
import { setIsFocusModalOpen, setUnsavedAnnotations } from "store/slices/FocusModal/focusModalSlice";
import { ListResponse } from "types/api";
import { Detector } from "types/detectors";
import { ImageQuery } from "types/imageQueries";
import { flaggedFilter, redirectToDataPage } from "utils/redirectDataPage";

import { STATUS } from "./Activity";
import { FlaggedMenu } from "./FlaggedMenu";

interface FlaggedProps {
  getPosichecks: (route?: string | undefined) => Promise<ListResponse<ImageQuery>>;
  posichecks: ListResponse<ImageQuery> | undefined;
  status: JSX.Element | null;
  relabeledPosicheck: ImageQuery | undefined;
  hasRelabeledPosicheck: boolean;
  handleChangeOrdering: (value: string) => void;
  orderingParam: string;
  detector: Detector | undefined;
}

export const Flagged = React.memo((props: FlaggedProps) => {
  const {
    getPosichecks,
    posichecks,
    status,
    relabeledPosicheck,
    hasRelabeledPosicheck,
    handleChangeOrdering,
    orderingParam,
    detector,
  } = props;
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [showMore, setShowMore] = useState<boolean>(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [gridIndex, setGridIndex] = useState(0);

  useEffect(() => {
    if (posichecks?.results) {
      setShowMore(posichecks?.count > posichecks?.results.length);
    } else {
      setShowMore(false);
    }
  }, [posichecks]);

  const handleOpenDataDetails = (currentIndex: number) => {
    setGridIndex(currentIndex);
    dispatch(setUnsavedAnnotations(false));
    dispatch(setIsFocusModalOpen(true));
    setIsModalOpen(true);
  };

  const closeFocusModal = () => {
    getPosichecks();
    setIsModalOpen(false);
    dispatch(setIsFocusModalOpen(false));
  };

  const showPosichecks = posichecks?.results?.length;

  return (
    status || (
      <Box>
        <FlaggedMenu
          getPosichecks={getPosichecks}
          handleChangeOrdering={handleChangeOrdering}
          orderingParam={orderingParam}
          detector={detector}
          showPosichecks={showPosichecks}
          setLoading={setLoading}
        />
        <Box sx={{ height: "4px", mt: "4px", mb: "2px" }}>{loading && <LinearProgress />}</Box>
        <Stack className="space-y-4 mt-5">
          {relabeledPosicheck ? (
            <Slide direction="left" in={hasRelabeledPosicheck} timeout={500} mountOnEnter unmountOnExit>
              <div className="relative">
                <FlaggedListItem
                  posicheck={relabeledPosicheck}
                  relabeled
                  key={relabeledPosicheck.iq_id}
                  handleOpenDataDetails={() => handleOpenDataDetails(0)}
                />
                <CheckIcon className="absolute text-green-500 top-0 h-[100%] w-[100%] opacity-100" />
              </div>
            </Slide>
          ) : null}

          {showPosichecks ? (
            posichecks?.results?.map((posicheck, index) => (
              <FlaggedListItem
                posicheck={posicheck}
                key={posicheck.iq_id}
                handleOpenDataDetails={() => handleOpenDataDetails(index)}
              />
            ))
          ) : (
            <Box className="flex justify-center mt-5">
                <Typography variant="subtitle">
                  No Items Flagged
                </Typography>
            </Box>
          )}
        </Stack>
        {showMore && detector ? (
          <SeeAllLink
            href={redirectToDataPage({ detectorId: detector?.id, filterOptions: flaggedFilter.unsure })}
            text="See All Queries"
          />
        ) : undefined}
        {showPosichecks && isModalOpen && detector ? (
          <BoundingBoxesProvider>
            <QueryDetailsModal
              showDialog={isModalOpen}
              loading={loading}
              getData={getPosichecks}
              rows={posichecks?.results!}
              rowCount={posichecks?.results && posichecks?.results.length!}
              selectedRowIndex={gridIndex}
              setSelectedRowIndex={setGridIndex}
              onClose={closeFocusModal}
              page={1}
              pageSize={12}
              setPage={() => {}}
              setPageSize={() => {}}
              mode={STATUS.FLAGGED}
              detector={detector}
            />
          </BoundingBoxesProvider>
        ) : null}
      </Box>
    )
  );
});